import React, { useEffect, useState } from 'react';
import UILoadingPage from '../../../../components/UI/UILoadingPage/UILoadingPage';
import { USER_ROLE_EDITING_MANAGER } from '../../../../utils/Config/Constants';
import './styles/mobile.scss';
import './styles/desktop.scss';
import UIListInputRow from '../../../../components/UI/UIListInputRow';
import * as EmailValidator from 'email-validator';
import Queries from '../../../../utils/API/Queries';
import UIModal from '../../../../components/UI/UIModal';
import { EDITING_MANAGER_COMPANY_INFO_TAB, EDITING_MANAGER_EDITORS_TAB, EDITING_MANAGER_INFO_TAB } from '../../../../utils/Config/MenuBodyTabs';
import Helmet from 'react-helmet';
import UIIconButton from '../../../../components/UI/UIIconButton';
import UIMenuBody, { filterSelectedItem } from '../../../../components/UI/UIMenuBody';
import UIOverlayCard from '../../../../components/UI/UIOverlayCard';
import { UI_LABELED_INPUT_PHONE_NUMBER_TYPE, UI_LABELED_INPUT_TEXT_TYPE } from '../../../../components/UI/UILabeledInput/config';
import { toast } from 'react-toastify';
import { useApolloClient } from 'react-apollo';
import { useLocation, useRouteMatch } from 'react-router-dom';
import IndividualEditingCompanyInfo from './IndividualEditingCompanyInfo';
import IndividualEditingCompanyEditors from './IndividualEditingCompanyEditors';
import { UI_ICON_BUTTON_FILL_TYPE } from '../../../../components/UI/UIIconButton/config';

export default function IndividualEditingCompany({}: IndividualEditingCompanyProps) {
	const [editingCompany, setEditingCompany] = useState<EditingCompanyType>();

	const [managerName, setManagerName] = useState<string>('');
	const [managerEmail, setManagerEmail] = useState<string>('');
	const [managerPhoneNumber, setManagerPhoneNumber] = useState<string>('');
	const [managerPassword, setManagerPassword] = useState<string>('');
	const [addManagerOverlay, setAddManagerOverlay] = useState<boolean>(false);
	const [isSaving, setIsSaving] = useState<object>({});
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const client = useApolloClient();
	const match = useRouteMatch();
	const location = useLocation();

	const queryEditingCompany = async (optionalCompletion?: Function) => {
		const response = await client
			.query({
				query: Queries.GET_EDITING_COMPANY,
				variables: { id: match.params['id'] }
			})
			.catch(errorCatch);
		const { editingCompany } = response['data'];
		const { editingManager } = editingCompany;

		if (editingCompany) {
			setEditingCompany(editingCompany);
			setManagerName(editingManager ? editingManager.name : '');
			setManagerEmail(editingManager ? editingManager.email : '');
			setManagerPhoneNumber(editingManager ? editingManager.phoneNumber : '');
			if (optionalCompletion) {
				optionalCompletion();
			}
		}
	};

	useEffect(() => {
		(async () => {
			await queryEditingCompany();
		})();
	}, []);

	if (!editingCompany) return null;

	const errorCatch = (error) => {
		setIsLoading(false);
		toast.error(error.message);
	};

	const getMenuItems = () => {
		return [EDITING_MANAGER_COMPANY_INFO_TAB, EDITING_MANAGER_EDITORS_TAB, EDITING_MANAGER_INFO_TAB];
	};

	const createEditingCompanyManager = async () => {
		if (!isLoading) {
			if (!managerName || managerName.trim().length === 0) {
				toast.info('Please enter a manager name!');
				return;
			} else if (!managerEmail || !EmailValidator.validate(managerEmail.trim())) {
				toast.info('Please enter a valid email!');
				return;
			} else if (!managerPhoneNumber || managerPhoneNumber.trim().length === 0) {
				toast.info('Please enter a manager phone number!');
				return;
			} else if (!managerPassword || managerPassword.trim().length === 0) {
				toast.info('Please a manager password!');
				return;
			}
			setIsLoading(true);

			const response = await client.mutate({
				mutation: Queries.CREATE_USER_MUTATION,
				variables: {
					role: USER_ROLE_EDITING_MANAGER,
					name: managerName,
					email: managerEmail,
					phoneNumber: managerPhoneNumber,
					password: managerPassword,
					editingCompany: editingCompany.id,
					requiresPasswordChange: true
				}
			});
			const { signup } = response['data'];
			if (signup) {
				client
					.mutate({
						mutation: Queries.UPDATE_EDITING_COMPANY,
						variables: { id: editingCompany.id, managerId: signup.id }
					})
					.then(async (result) => {
						const { updateEditingCompany } = result.data;
						if (updateEditingCompany) {
							await queryEditingCompany();
							setAddManagerOverlay(false);
							setManagerPassword('');
							toast.success(`Company Manager added: ${managerName}!`);
						}
					})
					.catch(errorCatch);
			}
		}
	};

	const onChangeManagerInfo = (key, value) => {
		let { editingManager } = editingCompany;

		if (key === 'name') {
			editingManager.name = value;
			editingCompany.editingManager = editingManager;
			setEditingCompany(editingCompany);
		} else if (key === 'email') {
			editingManager.email = value;
			editingCompany.editingManager = editingManager;
			setEditingCompany(editingCompany);
		} else if (key === 'phoneNumber') {
			editingManager.phoneNumber = value;
			editingCompany.editingManager = editingManager;
			setEditingCompany(editingCompany);
		} else {
			return;
		}
		setIsSaving({ ...isSaving, [key]: true });

		client
			.mutate({
				mutation: Queries.UPDATE_USER_MUTATION,
				variables: { id: editingManager.id, [key]: value }
			})
			.then(async (response) => {
				const { updateUser } = response['data'];
				if (!updateUser) {
					toast.error('Error updating editing company manager name!');
				} else {
					await queryEditingCompany();
					delete isSaving[key];
					setIsSaving(isSaving);
					toast.success('Updated editing company name!');
				}
			})
			.catch(errorCatch);
	};

	const renderSelectedPage = () => {
		const { editingManager } = editingCompany;

		const selectedPage = filterSelectedItem(getMenuItems(), location);
		switch (selectedPage) {
			case EDITING_MANAGER_COMPANY_INFO_TAB:
				return <IndividualEditingCompanyInfo editingCompanyId={editingCompany.id} />;
			case EDITING_MANAGER_EDITORS_TAB:
				return <IndividualEditingCompanyEditors editingCompanyId={editingCompany.id} />;
			case EDITING_MANAGER_INFO_TAB:
				return (
					<div className='managerInfo'>
						<p className='page-title'>Manager Info</p>
						{editingManager ? (
							<div>
								<UIListInputRow
									containerStyle={{ marginBottom: 8 }}
									type={UI_LABELED_INPUT_TEXT_TYPE}
									label={'name'}
									onChangeValue={setManagerName}
									value={managerName}
									isLoading={isSaving['companyManagerName']}
									savedValue={editingManager.name}
									onSave={() => onChangeManagerInfo('name', managerName)}
									placeholder={'Name...'}
								/>
								<UIListInputRow
									containerStyle={{ marginBottom: 8 }}
									type={UI_LABELED_INPUT_TEXT_TYPE}
									label={'e-mail'}
									onChangeValue={setManagerEmail}
									value={managerEmail}
									isLoading={isSaving['companyManagerEmail']}
									savedValue={editingManager.email}
									onSave={() => onChangeManagerInfo('email', managerEmail)}
									placeholder={'Email...'}
								/>
								<UIListInputRow
									type={UI_LABELED_INPUT_PHONE_NUMBER_TYPE}
									label={'phone number'}
									onChangeValue={setManagerPhoneNumber}
									value={managerPhoneNumber}
									isLoading={isSaving['companyManagerNumber']}
									savedValue={editingManager.phoneNumber}
									onSave={() => onChangeManagerInfo('phoneNumber', managerPhoneNumber)}
									placeholder={'Phone number...'}
								/>
							</div>
						) : (
							<div className='noManager'>
								<p className='noManager__text'>There is no editing manager assigned. Please add one now.</p>
								<UIIconButton
									dark
									style={{ marginTop: 16 }}
									type={UI_ICON_BUTTON_FILL_TYPE}
									text={'create a manager'}
									onClick={() => setAddManagerOverlay(true)}
								/>
							</div>
						)}
					</div>
				);
			default:
				return null;
		}
	};

	const renderAddManagerModal = () => {
		const hideModal = () => setAddManagerOverlay(false);

		return (
			<UIModal condition={addManagerOverlay}>
				<UIOverlayCard hideModal={hideModal} title={'Add an editing company manager'}>
					<UIListInputRow
						label={'MANAGER NAME'}
						containerStyle={{ marginTop: 8, width: '100%' }}
						style={{ width: '100%' }}
						type={UI_LABELED_INPUT_TEXT_TYPE}
						onChangeValue={setManagerName}
						value={managerName}
						placeholder={'Manager name...'}
					/>
					<UIListInputRow
						label={'MANAGER EMAIL'}
						containerStyle={{ marginTop: 8, width: '100%' }}
						style={{ width: '100%' }}
						type={UI_LABELED_INPUT_TEXT_TYPE}
						onChangeValue={setManagerEmail}
						value={managerEmail}
						placeholder={'Manager email...'}
					/>
					<UIListInputRow
						label={'MANAGER PHONE NUMBER'}
						containerStyle={{ marginTop: 8, width: '100%' }}
						style={{ width: '100%' }}
						type={UI_LABELED_INPUT_PHONE_NUMBER_TYPE}
						onChangeValue={setManagerPhoneNumber}
						value={managerPhoneNumber}
						placeholder={'Manager phone number...'}
					/>
					<UIListInputRow
						label={'MANAGER PASSWORD'}
						containerStyle={{ marginTop: 8, width: '100%' }}
						style={{ width: '100%' }}
						type={UI_LABELED_INPUT_TEXT_TYPE}
						onChangeValue={setManagerPassword}
						value={managerPassword}
						placeholder={'Manager password...'}
					/>
					<div className='uiCard__buttons' style={{ marginTop: 16 }}>
						<UIIconButton dark isLoading={isLoading} type={UI_ICON_BUTTON_FILL_TYPE} text={'create'} onClick={createEditingCompanyManager} />
					</div>
				</UIOverlayCard>
			</UIModal>
		);
	};

	if (!editingCompany) {
		return <UILoadingPage />;
	}

	return (
		<UIMenuBody menuItems={getMenuItems()}>
			<>
				<Helmet>
					<title>{editingCompany.name} | Pineapple Labs</title>
					<meta property='og:title' content={`${editingCompany.name} | Pineapple Labs`} />
				</Helmet>
				{renderAddManagerModal()}
				{renderSelectedPage()}
			</>
		</UIMenuBody>
	);
}
